import { useEffect, useMemo, useRef, useState } from 'react';
import { PhoneLookupLocalCache } from './cache.local';
import { DssClient } from './dssClient';
import { PhoneEnricher } from './enricher';
import { PhoneEnrichmentTelemetry } from './telemetry';
import {
  CallHistoryItem,
  ENRICHMENT_STATUS,
  EnrichmentStatus,
  PhoneEnrichmentOptions,
  EnrichmentUpdate,
} from './types';
import type { ContactsIntegration } from './contacts';
import { areContactsReadyForEnrichment, CONTACTS_ENRICHMENT_READY_EVENT, CONTACTS_REFRESHED_EVENT, CONTACTS_DATA_READY_EVENT } from './contactsIntegration';

export interface EnrichedCallHistoryItem extends CallHistoryItem {
  enrichment?: {
    displayName?: string;
    entityId?: string;
    status: EnrichmentStatus;
  };
}

export interface EnrichmentHookResult {
  enrichedItems: EnrichedCallHistoryItem[];
  statusMap: Record<string, EnrichmentStatus>;
  pendingCount: number;
}

const DEFAULT_UNKNOWN_LABEL = 'Unknown';
const CONTACTS_WAIT_POLL_MS = 500;
/** Max ~3s wait for contacts resolver; enrichment proceeds without blocking on slow indexing. */
const CONTACTS_WAIT_MAX_ATTEMPTS = 6;

export function usePhoneEnrichment(
  callHistory: CallHistoryItem[],
  options: PhoneEnrichmentOptions
): EnrichmentHookResult {
  const isEnabled = options.enableEnrichment === true;

  const [enrichmentMap, setEnrichmentMap] = useState<Record<string, EnrichmentUpdate[string]>>({});
  const [statusMap, setStatusMap] = useState<Record<string, EnrichmentStatus>>({});
  const [contactsReady, setContactsReady] = useState<boolean>(false);

  const enricherRef = useRef<PhoneEnricher | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<PhoneLookupLocalCache | null>(null);

  // Early return if disabled
  const disabledResult = useMemo(
    () => ({
      enrichedItems: callHistory as EnrichedCallHistoryItem[],
      statusMap: {},
      pendingCount: 0,
    }),
    [callHistory]
  );

  // Wait for contacts to finish loading before the first enrichment pass (cold start).
  useEffect(() => {
    if (!isEnabled) {
      setContactsReady(false);
      return;
    }

    const waitForContacts = options.waitForContacts === true;
    const contactsRefreshSignal = options.contactsRefreshSignal ?? 0;

    const markContactsReady = () => setContactsReady(true);

    if (!waitForContacts) {
      markContactsReady();
      return;
    }

    if (areContactsReadyForEnrichment()) {
      markContactsReady();
      return;
    }

    setContactsReady(false);

    let cancelled = false;
    let attempts = 0;

    const tryMarkReady = () => {
      if (cancelled) {
        return true;
      }
      if (areContactsReadyForEnrichment()) {
        markContactsReady();
        return true;
      }
      return false;
    };

    const handleContactsAvailable = () => {
      tryMarkReady();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(CONTACTS_DATA_READY_EVENT, handleContactsAvailable);
      window.addEventListener(CONTACTS_ENRICHMENT_READY_EVENT, handleContactsAvailable);
    }

    if (tryMarkReady()) {
      return () => {
        cancelled = true;
        if (typeof window !== 'undefined') {
          window.removeEventListener(CONTACTS_DATA_READY_EVENT, handleContactsAvailable);
          window.removeEventListener(CONTACTS_ENRICHMENT_READY_EVENT, handleContactsAvailable);
        }
      };
    }

    const interval = setInterval(() => {
      attempts += 1;
      if (tryMarkReady() || attempts >= CONTACTS_WAIT_MAX_ATTEMPTS) {
        if (attempts >= CONTACTS_WAIT_MAX_ATTEMPTS) {
          markContactsReady();
        }
        clearInterval(interval);
      }
    }, CONTACTS_WAIT_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (typeof window !== 'undefined') {
        window.removeEventListener(CONTACTS_DATA_READY_EVENT, handleContactsAvailable);
        window.removeEventListener(CONTACTS_ENRICHMENT_READY_EVENT, handleContactsAvailable);
      }
    };
  }, [
    isEnabled,
    options.waitForContacts,
    options.contacts,
    options.contactsRefreshSignal,
  ]);

  useEffect(() => {
    if (!isEnabled) {
      enricherRef.current = null;
      return;
    }

    if (!options.dss) {
      console.warn('[Enrichment] Hook: DSS SDK not provided; enrichment disabled.');
      enricherRef.current = null;
      return;
    }

    const cache = new PhoneLookupLocalCache({
      userId: options.userId,
      ttlMs: options.ttlMs,
      maxEntries: options.maxEntries,
    });
    cacheRef.current = cache;

    const dssClient = new DssClient({
      dss: options.dss,
      batchDelayMs: options.batchDelayMs,
      lookupTimeoutMs: options.lookupTimeoutMs,
    });

    const telemetry =
      options.telemetry ||
      new PhoneEnrichmentTelemetry({
        enableTelemetry: false,
      });

    // Get contacts resolver from options (optional)
    const contactsResolver = (options as any).contacts?.resolver;

    enricherRef.current = new PhoneEnricher(
      cache, 
      dssClient, 
      {
        unknownLabel: options.unknownLabel || DEFAULT_UNKNOWN_LABEL,
        telemetry,
        enableDirectorySearchFallback: options.enableDirectorySearchFallback,
        enableOutlookLookup: options.enableOutlookLookup,
      },
      contactsResolver  // Pass contacts resolver (optional)
    );

    return () => {
      enricherRef.current?.dispose();
      enricherRef.current = null;
      cacheRef.current = null;
    };
  }, [isEnabled, options.dss, options.userId, options.ttlMs, options.maxEntries, options.batchDelayMs, options.lookupTimeoutMs, options.telemetry, options.unknownLabel, options.contacts, contactsReady, options.contactsRefreshSignal]);

  // Listen for contacts refresh events and clear contacts entries from cache
  useEffect(() => {
    if (!isEnabled || !cacheRef.current) {
      return;
    }

    const handleContactsRefresh = () => {
      cacheRef.current?.clearContactsEntries();
    };

    window.addEventListener(CONTACTS_REFRESHED_EVENT, handleContactsRefresh);
    
    return () => {
      window.removeEventListener(CONTACTS_REFRESHED_EVENT, handleContactsRefresh);
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled || !enricherRef.current || !contactsReady) {
      return;
    }

    if (!callHistory || callHistory.length === 0) {
      setEnrichmentMap({});
      setStatusMap({});
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Mark all items as enriching to start
    const initialStatus: Record<string, EnrichmentStatus> = {};
    callHistory.forEach((item) => {
      initialStatus[item.id] = ENRICHMENT_STATUS.ENRICHING;
    });
    setStatusMap(initialStatus);

    enricherRef.current
      .enrich(
        callHistory,
        (updates: EnrichmentUpdate) => {
          setEnrichmentMap((prev) => {
            const next = { ...prev };
            Object.entries(updates).forEach(([id, update]) => {
              const prior = prev[id];
              if (
                update.status === ENRICHMENT_STATUS.MISS &&
                !update.displayName &&
                prior?.displayName
              ) {
                next[id] = { ...prior, status: update.status };
              } else {
                next[id] = { ...prior, ...update };
              }
            });
            return next;
          });
          const statusUpdates: Record<string, EnrichmentStatus> = {};
          Object.keys(updates).forEach((id) => {
            statusUpdates[id] = updates[id].status;
          });
          setStatusMap((prev) => ({ ...prev, ...statusUpdates }));
        },
        abortControllerRef.current?.signal
      )
      .catch((error) => {
        console.error('[Enrichment] Hook: Enrichment error', error);
      });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [callHistory, isEnabled, contactsReady, options.contactsRefreshSignal]);

  if (!isEnabled) {
    return disabledResult;
  }

  const enrichedItems: EnrichedCallHistoryItem[] = useMemo(
    () =>
      callHistory.map((item) => ({
        ...item,
        enrichment: enrichmentMap[item.id],
      })),
    [callHistory, enrichmentMap]
  );

  const pendingCount = useMemo(
    () =>
      Object.values(statusMap).filter((status) => status === ENRICHMENT_STATUS.ENRICHING).length,
    [statusMap]
  );

  return { enrichedItems, statusMap, pendingCount };
}
